<template>
  <div>
    <!-- Bandeau pour Android / Chrome / Edge (beforeinstallprompt natif) -->
    <v-bottom-sheet
      v-model="showInstallBanner"
      persistent
      :overlay-opacity="0.3"
    >
      <v-card class="pwa-card" tile>
        <div class="pwa-banner">
          <div class="pwa-icon-wrap">
            <img :src="logoSrc" alt="Gwassou" class="pwa-logo" />
          </div>
          <div class="pwa-text">
            <div class="pwa-title">{{ $t("Installer Gwassou") }}</div>
            <div class="pwa-desc">
              {{ $t("Ajoutez l'application sur votre ecran d'accueil pour un acces direct, sans passer par le navigateur.") }}
            </div>
          </div>
        </div>
        <div class="pwa-actions">
          <v-btn text color="grey darken-1" @click="dismissInstall">
            {{ $t("Plus tard") }}
          </v-btn>
          <v-btn color="primary" depressed @click="triggerInstall">
            <v-icon left small>mdi-download</v-icon>
            {{ $t("Installer") }}
          </v-btn>
        </div>
      </v-card>
    </v-bottom-sheet>

    <!-- Dialog pour iOS (Safari ne supporte pas beforeinstallprompt) -->
    <v-dialog v-model="showIosDialog" max-width="420">
      <v-card>
        <v-card-title class="text-h6">
          <v-icon left color="primary">mdi-apple</v-icon>
          {{ $t("Installer sur iPhone / iPad") }}
        </v-card-title>
        <v-card-text>
          <div class="text-body-2 mb-3">
            {{ $t("Pour installer Gwassou sur votre appareil Apple :") }}
          </div>
          <ol class="pl-4 ios-steps">
            <li>
              {{ $t("Touchez l icone") }}
              <v-icon small color="primary">mdi-export-variant</v-icon>
              <strong>{{ $t("Partager") }}</strong>
              {{ $t("en bas de Safari") }}
            </li>
            <li>
              {{ $t("Faites defiler et choisissez") }}
              <strong>{{ $t("Sur l ecran d accueil") }}</strong>
              <v-icon small color="primary">mdi-plus-box-outline</v-icon>
            </li>
            <li>
              {{ $t("Validez avec") }}
              <strong>{{ $t("Ajouter") }}</strong>
            </li>
          </ol>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dismissIos">{{ $t("Plus tard") }}</v-btn>
          <v-btn color="primary" depressed @click="dismissIos">{{ $t("J ai compris") }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import logoWhite from "@/assets/img/custom/logo-white.png"

const STORAGE_KEY = "pwa-install-dismissed-at"
const SNOOZE_DAYS = 7

export default {
  name: "PwaInstallPrompt",

  data() {
    return {
      deferredPrompt: null,
      showInstallBanner: false,
      showIosDialog: false,
      logoSrc: logoWhite,
    }
  },

  computed: {
    isMobile() {
      const ua = (navigator.userAgent || "").toLowerCase()
      const byUa = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(ua)
      const byTouch = "ontouchstart" in window && window.innerWidth <= 900
      return byUa || byTouch
    },

    isIOS() {
      const ua = navigator.userAgent || ""
      return /iPhone|iPad|iPod/i.test(ua) && !/CriOS|FxiOS|OPiOS|EdgiOS/i.test(ua)
    },

    isAlreadyInstalled() {
      // standalone display = lance depuis l ecran d accueil
      const standalone =
        (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) ||
        window.navigator.standalone === true
      return standalone
    },

    isRecentlyDismissed() {
      const at = Number(localStorage.getItem(STORAGE_KEY) || 0)
      if (!at) return false
      const ageMs = Date.now() - at
      return ageMs < SNOOZE_DAYS * 24 * 60 * 60 * 1000
    },
  },

  mounted() {
    // Mode debug : ?pwa-test=1 force l affichage (Android-like)
    //              ?pwa-test=ios force le dialog iOS
    const testMode = new URLSearchParams(window.location.search).get("pwa-test")
    if (testMode === "ios") {
      this.showIosDialog = true
      return
    }
    if (testMode === "1" || testMode === "android") {
      this.deferredPrompt = {
        prompt: () => Promise.resolve(),
        userChoice: Promise.resolve({ outcome: "accepted" }),
      }
      this.showInstallBanner = true
      return
    }

    if (this.isAlreadyInstalled) return
    if (!this.isMobile) return
    if (this.isRecentlyDismissed) return

    // On ecoute toujours l'event natif pour pouvoir declencher l'install
    // systeme (Android/Chrome) si Chrome decide de le proposer.
    window.addEventListener("beforeinstallprompt", this.onBeforeInstallPrompt)
    window.addEventListener("appinstalled", this.onInstalled)

    // ---- Affichage IMMEDIAT ----
    // On detecte mobile -> on propose tout de suite, sans attendre que
    // Chrome decide (les criteres d'engagement peuvent retarder l'event).
    if (this.isIOS) {
      // iOS Safari : pas d'event natif possible -> dialog d'instructions
      this.showIosDialog = true
    } else {
      // Android / Chromium : on affiche le bandeau tout de suite.
      // Le bouton "Installer" utilisera deferredPrompt s'il arrive plus
      // tard, sinon il affichera les instructions manuelles.
      this.showInstallBanner = true
    }
  },

  beforeDestroy() {
    window.removeEventListener("beforeinstallprompt", this.onBeforeInstallPrompt)
    window.removeEventListener("appinstalled", this.onInstalled)
  },

  methods: {
    onBeforeInstallPrompt(e) {
      // On capture l'event pour pouvoir l'utiliser plus tard sur le bouton
      // "Installer". Le bandeau peut deja etre visible (affichage immediat
      // au mount) - dans ce cas on enrichit juste la capacite d'install natif.
      e.preventDefault()
      this.deferredPrompt = e
      // Si pour une raison X le bandeau n'a pas ete ouvert (mobile detecte
      // tardivement), on le declenche.
      if (!this.showInstallBanner && !this.showIosDialog && !this.isAlreadyInstalled) {
        this.showInstallBanner = true
      }
    },

    async triggerInstall() {
      // Si Chrome a fourni l'event natif -> on l'utilise (install systeme)
      if (this.deferredPrompt && typeof this.deferredPrompt.prompt === "function") {
        this.showInstallBanner = false
        try {
          this.deferredPrompt.prompt()
          const choice = await this.deferredPrompt.userChoice
          if (choice && choice.outcome !== "accepted") {
            this.snooze()
          }
        } catch (e) {
          // ignore
        } finally {
          this.deferredPrompt = null
        }
        return
      }

      // Sinon : pas d'install natif disponible -> on bascule sur les
      // instructions manuelles (memes que iOS, adaptees au navigateur).
      this.showInstallBanner = false
      this.showIosDialog = true
      return
    },

    dismissInstall() {
      this.showInstallBanner = false
      this.snooze()
    },

    dismissIos() {
      this.showIosDialog = false
      this.snooze()
    },

    snooze() {
      try {
        localStorage.setItem(STORAGE_KEY, String(Date.now()))
      } catch (e) {
        // localStorage indisponible (mode prive iOS) - silencieux
      }
    },

    onInstalled() {
      this.showInstallBanner = false
      this.showIosDialog = false
      this.deferredPrompt = null
    },
  },
}
</script>

<style scoped>
.pwa-card {
  padding: 16px 20px 12px;
}
.pwa-banner {
  display: flex;
  align-items: flex-start;
  gap: 14px;
}
.pwa-icon-wrap {
  flex-shrink: 0;
  width: 52px;
  height: 52px;
  border-radius: 12px;
  background: #1c2437;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
}
.pwa-logo {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}
.pwa-text {
  flex: 1;
  min-width: 0;
}
.pwa-title {
  font-size: 17px;
  font-weight: 700;
  color: #1c2437;
  margin-bottom: 4px;
  line-height: 1.2;
}
.pwa-desc {
  font-size: 13.5px;
  color: #5f6368;
  line-height: 1.4;
}
.pwa-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px solid #eee;
}
.ios-steps {
  line-height: 2;
}
.ios-steps strong {
  margin: 0 4px;
}
</style>
